<template>
  <div class="fv-row">
    <!-- Thumbnail: tapping opens the video full screen. Static first-frame poster + play overlay. -->
    <div class="fv-thumb" @click="$emit('open', video)">
      <video :src="video.url" preload="metadata" muted playsinline></video>
      <span class="material-icons fv-play">play_circle</span>
      <span v-if="durationLabel" class="fv-duration">{{ durationLabel }}</span>
    </div>

    <div class="fv-info">
      <div class="fv-info-top">
        <div class="fv-titles">
          <div class="fv-title fv-title--clickable" :title="title" @click="$emit('open', video)">{{ title }}</div>
          <div v-if="address" class="fv-address">{{ address }}</div>
        </div>
        <div class="fv-row-actions">
          <button type="button" class="fv-circle" :title="$t('shared.share')" @click="$emit('share', video)">
            <span class="material-icons">share</span>
          </button>
          <button
            v-if="canDelete"
            type="button"
            class="fv-circle fv-circle--danger"
            :title="$t('shared.delete')"
            @click="$emit('delete', video)"
          >
            <span class="material-icons">delete_outline</span>
          </button>
        </div>
      </div>

      <div class="fv-meta">
        <span class="material-icons fv-meta-icon">{{ orientationIcon(video.aspect) }}</span>
        <span>{{ orientationLabel(video.aspect) }}</span>
        <template v-if="sizeLabel"><span class="fv-dot">·</span><span>{{ sizeLabel }}</span></template>
        <template v-if="dateLabel"><span class="fv-dot">·</span><span>{{ dateLabel }}</span></template>
      </div>

      <a v-if="subjectLink" :href="subjectLink.url" class="fv-view" target="_blank" rel="noopener">
        <span class="material-icons">open_in_new</span> {{ subjectLink.label }}
      </a>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { orientationLabel, orientationIcon, formatFileSize, formatRelativeDate, formatDuration, videoCardTitle, videoCardLink, videoCardAddress } from './formatters';

const props = defineProps({
  video: { type: Object, required: true },
  canDelete: { type: Boolean, default: false },
});

defineEmits(['delete', 'share', 'open']);

const title = computed(() => videoCardTitle(props.video));
// Per kind: a trailer's address arrives on hikeShortAddress, a flyover's on recordingShortAddress.
const address = computed(() => videoCardAddress(props.video));
const sizeLabel = computed(() => formatFileSize(props.video.fileSizeBytes));
const dateLabel = computed(() => formatRelativeDate(props.video.createdAt));
const durationLabel = computed(() => formatDuration(props.video.durationSeconds));
const subjectLink = computed(() => videoCardLink(props.video));
</script>

<style scoped>
.fv-row {
  display: flex;
  gap: 14px;
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 10px;
  transition: background 0.2s, border-color 0.2s;
}
.fv-row:hover {
  background: #252530;
  border-color: rgba(255, 255, 255, 0.12);
}
.fv-thumb {
  position: relative;
  flex: 0 0 auto;
  width: 128px;
  height: 92px;
  background: #000;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.fv-thumb video {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
}
.fv-play {
  position: absolute;
  color: rgba(255, 255, 255, 0.92);
  font-size: 40px;
  pointer-events: none;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.6);
}
.fv-duration {
  position: absolute;
  right: 6px;
  bottom: 6px;
  background: rgba(0, 0, 0, 0.78);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 5px;
}
.fv-info {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.fv-info-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.fv-titles {
  min-width: 0;
}
.fv-title {
  color: rgba(255, 255, 255, 0.92);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fv-title--clickable {
  cursor: pointer;
}
.fv-title--clickable:hover {
  text-decoration: underline;
}
.fv-address {
  color: #8a9099;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}
.fv-row-actions {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
}
.fv-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.fv-circle:hover {
  background: rgba(255, 255, 255, 0.16);
}
.fv-circle .material-icons {
  font-size: 17px;
}
.fv-circle--danger {
  color: #ff6b6b;
}
.fv-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #8a9099;
  font-size: 12px;
  margin: 6px 0;
}
.fv-meta-icon {
  font-size: 15px;
}
.fv-dot {
  opacity: 0.6;
}
.fv-view {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #fbcd01;
  font-weight: 600;
  font-size: 11px;
  text-decoration: none;
  margin-top: auto;
}
.fv-view:hover {
  text-decoration: underline;
}
.fv-view .material-icons {
  font-size: 13px;
}
</style>
